import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, Progress } from '@backstage/core-components';
import { TextField, Typography, makeStyles } from '@material-ui/core';
import { useLocation } from 'react-router-dom';
import { useInsights } from '../catalog/pipeline/useInsights';
import { plural } from '../home/attention';
import { updatedLabel } from '../home/updatedLabel';
import { visuallyHidden } from '../visuallyHidden';
import { formatShare } from './scorecards';
import {
  MARK_LOOK,
  metOf,
  repoRowIdOf,
  type RepoRow,
  type RepoStandardSnapshot,
  type Rule,
} from './repositories';
import { StandardTrend } from './StandardTrend';
import { CollapsedNotices } from './CollapsedNotices';

const useStyles = makeStyles(theme => ({
  stack: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5),
    minWidth: 0,
  },
  figures: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
    border: '1px solid var(--ui-border)',
    borderRadius: 8,
    background: 'var(--ui-bg-elevated)',
    overflow: 'hidden',
  },
  figure: {
    padding: theme.spacing(1.5, 2),
    minWidth: 0,
    borderRight: '1px solid var(--ui-border)',
    '&:last-child': { borderRight: 'none' },
    [theme.breakpoints.down('xs')]: {
      '&:nth-child(2)': { borderRight: 'none' },
      '&:nth-child(-n+2)': { borderBottom: '1px solid var(--ui-border)' },
    },
  },
  label: { fontSize: '0.8rem', color: 'var(--ui-text-muted)' },
  value: {
    fontSize: '1.5rem',
    fontWeight: 600,
    lineHeight: 1.4,
    color: 'var(--ui-text)',
  },
  sub: { fontSize: '0.8rem', color: 'var(--ui-text-muted)' },
  // One row on a desktop: the fields share the width, and a long choice is
  // cut short inside its field rather than widening it. Two to a row below.
  filters: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, minmax(0, 1fr)) auto',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
  },
  select: { minWidth: 0 },
  count: {
    fontSize: '0.8rem',
    color: 'var(--ui-text-muted)',
    whiteSpace: 'nowrap',
  },
  // The table scrolls sideways inside its own box, so the page never does —
  // positioned, for the words kept for screen readers (scorecards AC-8).
  tableBox: {
    position: 'relative',
    border: '1px solid var(--ui-border)',
    borderRadius: 8,
    background: 'var(--ui-bg-elevated)',
    overflowX: 'auto',
    minWidth: 0,
  },
  table: {
    width: '100%',
    minWidth: 960,
    borderCollapse: 'collapse',
    fontSize: '0.875rem',
    color: 'var(--ui-text)',
    '& th, & td': {
      padding: theme.spacing(1, 1),
      borderBottom: '1px solid var(--ui-border)',
      textAlign: 'left',
    },
    '& thead th': {
      fontWeight: 400,
      fontSize: '0.8rem',
      color: 'var(--ui-text-muted)',
    },
    '& tbody tr:last-child > *': { borderBottom: 'none' },
  },
  ruleHead: { width: 32, textAlign: 'center' },
  adminHead: { background: 'var(--ui-bg-muted)' },
  repoCell: { minWidth: 220 },
  repoName: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontWeight: 600,
  },
  org: { fontWeight: 400, color: 'var(--ui-text-muted)' },
  toggle: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    padding: 0,
    border: 'none',
    borderRadius: 4,
    background: 'transparent',
    color: 'var(--ui-text-muted)',
    cursor: 'pointer',
    font: 'inherit',
    '&:hover': { background: 'var(--ui-bg-muted)' },
    '&:focus-visible': {
      outline: '2px solid var(--ui-primary)',
      outlineOffset: 1,
    },
  },
  chevron: {
    display: 'inline-block',
    transition: 'transform 120ms',
    '&[data-open="true"]': { transform: 'rotate(90deg)' },
  },
  verdict: { whiteSpace: 'nowrap', color: 'var(--ui-text-muted)' },
  mark: {
    display: 'block',
    textAlign: 'center',
    fontWeight: 700,
    cursor: 'default',
  },
  highlighted: {
    '& > *': { background: 'var(--ui-nav-active-bg)' },
  },
  details: {
    margin: 0,
    padding: theme.spacing(0.5, 0, 0.5, 4),
    listStyle: 'none',
    display: 'grid',
    gap: theme.spacing(0.5),
    '& li': { display: 'flex', gap: theme.spacing(1), alignItems: 'baseline' },
  },
  detailRule: { fontWeight: 600, whiteSpace: 'nowrap' },
  legendBox: {
    border: '1px solid var(--ui-border)',
    borderRadius: 8,
    background: 'var(--ui-bg-elevated)',
  },
  legend: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: theme.spacing(0.5, 2),
    margin: 0,
    padding: theme.spacing(1.25, 2),
    listStyle: 'none',
    fontSize: '0.8rem',
    color: 'var(--ui-text-muted)',
    '& b': { color: 'var(--ui-text)', marginRight: 4 },
  },
  key: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5, 2),
    margin: 0,
    padding: theme.spacing(1, 2),
    listStyle: 'none',
    borderTop: '1px solid var(--ui-border)',
    fontSize: '0.8rem',
    color: 'var(--ui-text-muted)',
  },
  hidden: visuallyHidden,
  message: { color: 'var(--ui-text-muted)', padding: theme.spacing(2, 0) },
}));

const Figure = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => {
  const classes = useStyles();
  return (
    <div className={classes.figure} role="group" aria-label={label}>
      <div className={classes.label}>{label}</div>
      {children}
    </div>
  );
};

const Figures = ({ snapshot }: { snapshot: RepoStandardSnapshot }) => {
  const classes = useStyles();
  const { teamRules, progress } = snapshot.summary;
  const asked = teamRules.met + teamRules.notMet;
  return (
    <section aria-label="Repository figures" className={classes.figures}>
      <Figure label="Meet every team rule">
        <div className={classes.value}>
          {asked ? formatShare(teamRules.met, asked) : '–'}
        </div>
        <div className={classes.sub}>
          {teamRules.met} of {plural(asked, 'repository', 'repositories')}
          {teamRules.unknown > 0 && `, ${teamRules.unknown} not measurable`}
        </div>
        <StandardTrend />
      </Figure>
      <Figure label="main protected">
        <div className={classes.value}>
          {progress.mainProtected.of
            ? formatShare(
                progress.mainProtected.protected,
                progress.mainProtected.of,
              )
            : '–'}
        </div>
        <div className={classes.sub}>
          {progress.mainProtected.protected} of {progress.mainProtected.of} ·
          lies with the org admin
        </div>
      </Figure>
      <Figure label="Shared gate">
        <div className={classes.value}>{progress.sharedGate}</div>
        <div className={classes.sub}>
          {progress.sharedGate === 1 ? 'repository calls' : 'repositories call'}{' '}
          node-ci.yml@v1
        </div>
      </Figure>
      <Figure label="Measured">
        <div className={classes.value}>{snapshot.summary.repos}</div>
        <div className={classes.sub}>
          repositories in{' '}
          {plural(
            snapshot.orgs.filter(o => o.state === 'read').length,
            'organisation',
            'organisations',
          )}
        </div>
      </Figure>
    </section>
  );
};

const MarkCell = ({
  rule,
  mark,
}: {
  rule: Rule;
  mark: RepoRow['marks'][Rule['id']];
}) => {
  const classes = useStyles();
  const look = MARK_LOOK[mark.state];
  const says = `${rule.title}: ${look.word}. ${mark.detail}`;
  return (
    <td>
      <span
        className={classes.mark}
        style={{ color: look.colour }}
        title={says}
        aria-label={says}
        role="img"
      >
        {look.glyph}
      </span>
    </td>
  );
};

/** What a repository's team rules come to, in words. */
const verdictOf = (row: RepoRow, rules: Rule[]): string => {
  if (row.empty) return 'Empty';
  if (row.teamRulesMet === true) return 'Meets every team rule';
  const missed = rules.filter(
    r => r.fixedBy === 'team' && row.marks[r.id].state === 'fail',
  ).length;
  if (missed > 0) return `Misses ${plural(missed, 'team rule', 'team rules')}`;
  return 'Not measurable';
};

const Row = ({
  row,
  rules,
  highlighted,
}: {
  row: RepoRow;
  rules: Rule[];
  highlighted: boolean;
}) => {
  const classes = useStyles();
  const [open, setOpen] = useState(highlighted);
  useEffect(() => {
    if (highlighted) setOpen(true);
  }, [highlighted]);
  const id = repoRowIdOf(row.slug);
  const unmapped = row.exemptions.filter(e => e.rules.length === 0);
  return (
    <>
      <tr id={id} className={highlighted ? classes.highlighted : undefined}>
        <th scope="row" className={classes.repoCell}>
          <div className={classes.repoName}>
            <button
              type="button"
              className={classes.toggle}
              aria-expanded={open}
              aria-controls={`${id}-rules`}
              aria-label={`What the rules found for ${row.slug}`}
              onClick={() => setOpen(o => !o)}
            >
              <span className={classes.chevron} data-open={open} aria-hidden>
                ›
              </span>
            </button>
            <Link to={row.url}>{row.name}</Link>
            <span className={classes.org}>{row.org}</span>
          </div>
        </th>
        <td className={classes.verdict}>{verdictOf(row, rules)}</td>
        {rules.map(rule => (
          <MarkCell key={rule.id} rule={rule} mark={row.marks[rule.id]} />
        ))}
      </tr>
      {open && (
        <tr id={`${id}-rules`}>
          <td colSpan={rules.length + 2}>
            <ul
              className={classes.details}
              aria-label={`What the rules found for ${row.slug}`}
            >
              {rules.map(rule => {
                const mark = row.marks[rule.id];
                const look = MARK_LOOK[mark.state];
                return (
                  <li key={rule.id}>
                    <span style={{ color: look.colour }} aria-hidden>
                      {look.glyph}
                    </span>
                    <span className={classes.detailRule}>
                      {rule.title}
                      <span className={classes.hidden}> {look.word}</span>
                    </span>
                    <span>
                      {mark.link ? (
                        <Link to={mark.link}>{mark.detail}</Link>
                      ) : (
                        mark.detail
                      )}
                    </span>
                  </li>
                );
              })}
              {unmapped.map(e => (
                <li key={e.text}>
                  <span aria-hidden>§</span>
                  <span className={classes.detailRule}>README</span>
                  <span>
                    Names no section of the standard, so exempts nothing: “
                    {e.text}”
                  </span>
                </li>
              ))}
            </ul>
          </td>
        </tr>
      )}
    </>
  );
};

const Table = ({
  snapshot,
  rows,
}: {
  snapshot: RepoStandardSnapshot;
  rows: RepoRow[];
}) => {
  const classes = useStyles();
  const { hash } = useLocation();
  const target = hash.replace(/^#/, '');
  const { rules } = snapshot;

  // A component's CI/CD tab links to its repository's row: once the rows
  // stand, bring it into view, since the browser looked for it before they did.
  useEffect(() => {
    if (!target) return;
    document.getElementById(target)?.scrollIntoView?.({ block: 'center' });
  }, [target, rows.length]);

  return (
    <div className={classes.tableBox}>
      <table className={classes.table} aria-label="Repositories">
        <thead>
          <tr>
            <th scope="col">Repository</th>
            <th scope="col">Team rules</th>
            {rules.map((rule, i) => (
              <th
                key={rule.id}
                scope="col"
                className={`${classes.ruleHead} ${
                  rule.fixedBy === 'admin' ? classes.adminHead : ''
                }`}
                title={`${rule.title} (${rule.section})`}
              >
                <span aria-hidden>{i + 1}</span>
                <span className={classes.hidden}>{rule.title}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <Row
              key={row.slug}
              row={row}
              rules={rules}
              highlighted={repoRowIdOf(row.slug) === target}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

/**
 * The rules, numbered as the columns are, with who fixes each and how many
 * meet it — and what each mark means. Above the table: the numbers are read
 * before the marks under them.
 */
const Legend = ({ snapshot }: { snapshot: RepoStandardSnapshot }) => {
  const classes = useStyles();
  const { rules } = snapshot;
  return (
    <div className={classes.legendBox}>
      <ol className={classes.legend} aria-label="Rules">
        {rules.map((rule, i) => {
          const { met, of } = metOf(snapshot.summary.rules[rule.id]);
          const exempt = snapshot.summary.rules[rule.id].exempt;
          return (
            <li key={rule.id}>
              <b>{i + 1}</b>
              {rule.title} · {rule.section} ·{' '}
              {rule.fixedBy === 'admin' ? 'org admin' : 'team'} ·{' '}
              {of ? `${met} of ${of} met` : 'not asked'}
              {exempt > 0 && `, ${exempt} exempt`}
            </li>
          );
        })}
      </ol>
      <ul className={classes.key} aria-label="Marks">
        {Object.entries(MARK_LOOK).map(([state, look]) => (
          <li key={state}>
            <span style={{ color: look.colour, fontWeight: 700 }} aria-hidden>
              {look.glyph}
            </span>{' '}
            {look.word}
          </li>
        ))}
      </ul>
    </div>
  );
};

const ALL = '';
/** The rule filter's choice for any rule the repository's team can fix. */
const ANY_TEAM_RULE = '*';

type Order = 'name' | 'missed';

/** How many rules its team can fix a repository misses. */
const missedCount = (row: RepoRow, rules: Rule[]) =>
  rules.filter(r => r.fixedBy === 'team' && row.marks[r.id].state === 'fail')
    .length;
const NO_OWNER = '-';

/** The team part of `group:default/platform`. */
const teamOf = (owner: string | undefined) =>
  owner ? owner.replace(/^group:(default\/)?/, '') : undefined;

/**
 * The Repositories tab of Scorecards (DX-225; promised in
 * specs/repository-standard.md): every repository of the organisations the
 * portal reads, measured against the CI/CD standard, rule by rule.
 *
 * Nothing is measured here. The github-insights backend reads the
 * repositories and measures them, and this tab shows what it answered — so a
 * component's CI/CD tab, which reads the same answer, cannot say otherwise.
 */
export const RepositoriesView = () => {
  const classes = useStyles();
  const snapshot = useInsights<RepoStandardSnapshot>('/repositories');
  const [query, setQuery] = useState('');
  const [org, setOrg] = useState(ALL);
  const [team, setTeam] = useState(ALL);
  const [missing, setMissing] = useState(ALL);
  const [order, setOrder] = useState<Order>('name');

  const data = snapshot.data;
  const orgs = useMemo(
    () => [...new Set((data?.repos ?? []).map(r => r.org))],
    [data],
  );
  const teams = useMemo(
    () =>
      [
        ...new Set(
          (data?.repos ?? [])
            .map(r => teamOf(r.owner))
            .filter((t): t is string => Boolean(t)),
        ),
      ].sort((a, b) => a.localeCompare(b, 'en', { sensitivity: 'base' })),
    [data],
  );
  const rows = useMemo(() => {
    const words = query.trim().toLocaleLowerCase('en-US');
    const narrowed = (data?.repos ?? []).filter(r => {
      if (words && !r.slug.toLocaleLowerCase('en-US').includes(words)) {
        return false;
      }
      if (org !== ALL && r.org !== org) return false;
      if (team === NO_OWNER && r.owner) return false;
      if (team !== ALL && team !== NO_OWNER && teamOf(r.owner) !== team) {
        return false;
      }
      if (missing === ANY_TEAM_RULE) return r.teamRulesMet === false;
      if (missing !== ALL) {
        return r.marks[missing as Rule['id']]?.state === 'fail';
      }
      return true;
    });
    // The backend orders by organisation and name; the most rules missed
    // first keeps that order among equals.
    const rules = data?.rules ?? [];
    return order === 'missed'
      ? [...narrowed].sort(
          (a, b) => missedCount(b, rules) - missedCount(a, rules),
        )
      : narrowed;
  }, [data, query, org, team, missing, order]);

  if (snapshot.loading) return <Progress />;
  if (snapshot.error || !data) {
    return (
      <Typography role="alert">
        Couldn't load the repositories from github-insights.
      </Typography>
    );
  }
  if (data.generatedAt === null) {
    return (
      <Typography className={classes.message}>
        The repositories have not been measured yet since the portal started.
        They are measured every six hours; check back in a few minutes.
      </Typography>
    );
  }

  const notices = data.orgs
    .filter(o => o.state === 'unread')
    .map(
      o =>
        `The repositories of ${o.org} could not be listed, so none of them is measured.`,
    );
  const ownersOf = (t: string) =>
    data.repos.filter(r => teamOf(r.owner) === t).length;

  return (
    <div className={classes.stack}>
      <Figures snapshot={data} />
      <CollapsedNotices
        id="repositories-not-measured"
        label="Not measured"
        summary={`${notices.length} ${
          notices.length === 1 ? 'organisation' : 'organisations'
        } not measured: ${data.orgs
          .filter(o => o.state === 'unread')
          .map(o => o.org)
          .join(', ')}`}
        notices={notices}
      />
      {data.repos.length > 0 && <Legend snapshot={data} />}
      <div className={classes.filters}>
        <TextField
          id="repositories-name-filter"
          size="small"
          variant="outlined"
          label="Repository"
          placeholder="Name"
          value={query}
          onChange={e => setQuery(e.target.value)}
          InputLabelProps={{ shrink: true }}
          className={classes.select}
          fullWidth
        />
        <TextField
          id="repositories-org-filter"
          select
          size="small"
          variant="outlined"
          label="Organisation"
          value={org}
          onChange={e => setOrg(e.target.value)}
          SelectProps={{ native: true }}
          InputLabelProps={{ shrink: true }}
          className={classes.select}
          fullWidth
        >
          <option value={ALL}>All organisations</option>
          {data.orgs.map(o => (
            <option key={o.org} value={o.org} disabled={!orgs.includes(o.org)}>
              {o.org} ({o.repos})
            </option>
          ))}
        </TextField>
        <TextField
          id="repositories-team-filter"
          select
          size="small"
          variant="outlined"
          label="Owner team"
          value={team}
          onChange={e => setTeam(e.target.value)}
          SelectProps={{ native: true }}
          InputLabelProps={{ shrink: true }}
          className={classes.select}
          fullWidth
        >
          <option value={ALL}>All teams</option>
          {teams.map(t => (
            <option key={t} value={t}>
              {t} ({ownersOf(t)})
            </option>
          ))}
          <option value={NO_OWNER}>
            No owner in the catalog ({data.repos.filter(r => !r.owner).length})
          </option>
        </TextField>
        <TextField
          id="repositories-rule-filter"
          select
          size="small"
          variant="outlined"
          label="Rule"
          value={missing}
          onChange={e => setMissing(e.target.value)}
          SelectProps={{ native: true }}
          InputLabelProps={{ shrink: true }}
          className={classes.select}
          fullWidth
        >
          <option value={ALL}>Any outcome</option>
          <option value={ANY_TEAM_RULE}>Misses a rule its team can fix</option>
          {data.rules.map((rule, i) => (
            <option key={rule.id} value={rule.id}>
              Misses {i + 1} · {rule.title}
            </option>
          ))}
        </TextField>
        <TextField
          id="repositories-order"
          select
          size="small"
          variant="outlined"
          label="Order"
          value={order}
          onChange={e => setOrder(e.target.value as Order)}
          SelectProps={{ native: true }}
          InputLabelProps={{ shrink: true }}
          className={classes.select}
          fullWidth
        >
          <option value="name">By name</option>
          <option value="missed">Most missed first</option>
        </TextField>
        <Typography className={classes.count} aria-live="polite">
          {rows.length === data.repos.length
            ? plural(rows.length, 'repository', 'repositories')
            : `${rows.length} of ${plural(
                data.repos.length,
                'repository',
                'repositories',
              )}`}
        </Typography>
      </div>
      {rows.length > 0 ? (
        <Table snapshot={data} rows={rows} />
      ) : (
        <Typography className={classes.message}>
          {data.repos.length
            ? 'No repository matches these filters.'
            : 'No repository was measured.'}
        </Typography>
      )}
      <Typography className={classes.sub}>
        Measured{' '}
        <time dateTime={data.generatedAt} title={data.generatedAt}>
          {updatedLabel(data.generatedAt)}
        </time>
        . How each rule is decided, and how to meet it:{' '}
        <Link to="/docs/default/component/backstage/repository-standard/">
          The CI/CD standard per repository
        </Link>
        .
      </Typography>
    </div>
  );
};
